import { Component, type ErrorInfo, type ReactNode } from 'react'
import { NOM } from '@/components/brand/nom'
import { Button } from '@/components/ui/Button'

/**
 * Un morceau du paquet a-t-il disparu ? — [DECISIONS §312].
 *
 * **Trois navigateurs, trois phrases**, et aucune n'est un code d'erreur : la
 * seule prise est le message. On accepte donc large — un faux positif propose
 * un rechargement qui ne fait aucun mal, un faux négatif laisse le candidat
 * devant deux boutons inertes.
 */
function estUnMorceauManquant(erreur: Error): boolean {
  const message = erreur.message.toLowerCase()

  return (
    message.includes('dynamically imported module') || // Chrome, Edge
    message.includes('importing a module script failed') || // Safari
    message.includes('error loading dynamically imported') || // Firefox
    message.includes('loading chunk')
  )
}

/**
 * Le filet — ce qui empêche un écran blanc.
 *
 * **Un écran blanc n'est jamais une issue acceptable.** Quand un composant
 * lève pendant le rendu, React démonte tout l'arbre : le candidat se retrouve
 * devant une page vide, sans message, sans bouton, et sans moyen de savoir
 * s'il a perdu son travail. C'est arrivé sur le quiz de section, et le seul
 * recours était de recharger l'application entière.
 *
 * **Il dit quoi faire, et laisse une sortie.** Deux boutons : réessayer — qui
 * remonte l'arbre sans recharger, donc sans reperdre la session — et revenir
 * à l'accueil. Le détail technique reste dépliable : il ne sert à rien au
 * candidat, mais il est ce qu'il copiera dans un signalement.
 *
 * **La `key` remonte l'arbre.** Sans elle, `reessayer` remettrait l'état à
 * `null` et React réutiliserait les mêmes instances — celles-là mêmes qui
 * viennent de lever.
 */
export class Filet extends Component<
  { children: ReactNode; onAccueil?: (() => void) | undefined },
  { erreur: Error | null; essai: number }
> {
  override state = { erreur: null as Error | null, essai: 0 }

  static getDerivedStateFromError(erreur: Error): { erreur: Error } {
    return { erreur }
  }

  override componentDidCatch(erreur: Error, infos: ErrorInfo): void {
    // La console reste le seul journal côté client. Sans cette trace, une
    // erreur attrapée devient une erreur invisible — pire que celle qui plante.
    console.error(`[${NOM}] rendu interrompu`, erreur, infos.componentStack)
  }

  override render(): ReactNode {
    const { erreur, essai } = this.state

    if (erreur === null) {
      return <div key={essai}>{this.props.children}</div>
    }

    /*
     * **Quand un morceau du paquet manque, les deux boutons sont inertes** —
     * [DECISIONS §312]. « Réessayer » remonte le MÊME écran, qui redemande le
     * MÊME fichier absent ; « Revenir à l'accueil » en monte un autre, qui
     * manque tout autant. Ben s'est retrouvé bloqué là le 21 août 2026, sans
     * aucune sortie depuis la page.
     *
     * La seule issue est un vrai rechargement : il redemande l'index au
     * serveur, qui nomme les morceaux de la version en ligne. C'est donc lui
     * qu'on propose, et lui seul — offrir trois boutons dont deux ne peuvent
     * pas marcher, c'est faire perdre deux essais à quelqu'un qui est déjà
     * coincé.
     */
    const morceauManquant = estUnMorceauManquant(erreur)

    return (
      <section className="flex flex-col gap-4 px-4 py-10">
        <h2 className="text-[20px] font-extrabold">
          {morceauManquant ? 'Une nouvelle version est en ligne' : 'Cet écran n’a pas pu s’afficher'}
        </h2>

        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          {morceauManquant
            ? 'L’application a été mise à jour pendant que cette page était ouverte. Rechargez pour reprendre où vous en étiez — votre progression est enregistrée.'
            : 'Votre progression est enregistrée : rien de ce que vous avez fait n’est perdu. Réessayez, ou revenez à l’accueil.'}
        </p>

        <div className="flex flex-wrap gap-2">
          {morceauManquant ? (
            <Button onClick={() => { window.location.reload() }}>Recharger l’application</Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  this.setState((avant) => ({ erreur: null, essai: avant.essai + 1 }))
                }}
              >
                Réessayer
              </Button>

              {this.props.onAccueil !== undefined && (
                <Button variant="outline" onClick={this.props.onAccueil}>
                  Revenir à l’accueil
                </Button>
              )}
            </>
          )}
        </div>

        <details className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
          <summary className="cursor-pointer">Détail technique</summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap">{erreur.message}</pre>
        </details>
      </section>
    )
  }
}
